// --------------------- Imports Globais ---------------------
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";
// --------------------- Imports Antd ---------------------
import { LinkOutlined } from "@ant-design/icons";
import { useMensagensService } from "../../services/mensagensService";
import {
  Col,
  Flex,
  Row,
  Button,
  Skeleton,
  Table,
  Typography,
  Form,
  Input,
  Modal,
  Space,
  Dropdown,
  message,
  Tag,
  Switch,
} from "antd";
import type { TableProps } from "antd/es/table";
import { MenuProps } from "antd/lib";
// --------------------- Imports Icons ---------------------
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
// --------------------- Imports Styles ---------------------
import { BotoesAcoes, Container } from "../ViewGerenciarCategoriaMensagens/styles";
// --------------------- Imports Hooks ---------------------
import { useTiposMensagem } from "../ViewGerenciarModeloMensagens/hooks/useTiposMensagens";
// --------------------- Imports Services ---------------------
import {
  criarTipoMensagemService,
  atualizarTipoMensagemService,
} from "../../services/tipoMensagemService";
import { categoriaMensagemService } from "../../services/categoriaMensagemService";
import { usePermissao } from "../../uteis/permissaoAdm/usePermissao";
import { validateToken } from "../../components/Globais";
import { useNavigate } from "react-router-dom";

export default function ViewModeloMensagens() {
  const { Title } = Typography;
  const isMobile = useMediaQuery("(max-width:600px)");
  const { sucesso, aviso, erro, contextHolder } = useMensagensService();
 
  const navigate = useNavigate();

  // Permissão
  const { isLoading: isTokenLoading, isValid } = usePermissao("ADM");

  // Pega categoriaId da query string
  const params = new URLSearchParams(window.location.search);
  const categoriaId = Number(params.get("categoria")) || 0;

  // Nome da categoria (para exibir no título)
  const [categoriaNome, setCategoriaNome] = useState<string>("");

  // Hook de tipos de mensagem
  const { tipos, isLoading, fetchTipos } = useTiposMensagem(categoriaId);

  // Estados do modal e formulário
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const tipoId = useRef<number>(0);

  

  // Buscar nome da categoria
  useEffect(() => {
    const carregarCategoria = async () => {
      if (categoriaId) {
        const categoria =
          await categoriaMensagemService.buscarCategoriaMensagemPorIdService(
            categoriaId
          );
        if (categoria) setCategoriaNome(categoria.nome);
      }
    };
    carregarCategoria();
  }, [categoriaId]);

  // Abrir modal
    const openModal = (tipo?: any) => {
      if (tipo) {
        setEditingId(tipo.id);
        form.setFieldsValue({
          ...tipo,
          linkPadrao: tipo.linkPadrao || "", // 👈 garante que apareça corretamente
          active: tipo.active === 1,         // 👈 transforma 0/1 em true/false
        });
      } else {
        setEditingId(null);
        form.resetFields();
      }
      setModalVisible(true);
    };


  // Salvar tipo de mensagem
      const handleSave = async () => {
      const values = await form.validateFields();

      if (!categoriaId) {
        await aviso("Categoria não informada!");
        return;
      }

      // 👇 Normaliza antes de enviar ao backend
      const payload = {
        ...values,
        active: values.active ? 1 : 0,
        categoriaMensagemId: categoriaId,
      };

      if (editingId) {
        await atualizarTipoMensagemService(editingId, payload);
        await sucesso("Tipo de mensagem atualizado com sucesso!");
      } else {
        await criarTipoMensagemService(payload);
        await sucesso("Tipo de mensagem criado com sucesso!");
      }

      setModalVisible(false);
      fetchTipos();
    };


  // Alternar ativo/inativo
  const toggleAtivo = async (id: number, ativoAtual: number) => {
    const novoValor = ativoAtual === 1 ? 0 : 1;
    await atualizarTipoMensagemService(id, { active: novoValor });
    await sucesso(`Tipo de mensagem ${novoValor ? "ativado" : "desativado"} com sucesso!`);
    fetchTipos();
  };

  // Ações do menu dropdown
  const handleMenuClick = async (key: string, row: any) => {
    
    switch (key) {
      case "edit":
        openModal(row);
        break;
      case "toggle":
        toggleAtivo(row.id, row.active);
        break;
      default:
        break;
    }
  };

  // Colunas da tabela — com menu dinâmico
  const columns: TableProps<any>["columns"] = [
    { title: "Nome", dataIndex: "nome", width: 160, ellipsis: true },
    { title: "Descrição", dataIndex: "descricao", width: 220, ellipsis: true },
    { title: "Título Padrão", dataIndex: "tituloPadrao", width: 180, ellipsis: true },
    {
      title: "Conteúdo Padrão",
      dataIndex: "conteudoPadrao",
      width: 300,
      ellipsis: true,
      render: (text: string) =>
        text && text.length > 100 ? text.slice(0, 100) + "..." : text,
    },
    {
      title: "Ativo",
      dataIndex: "active",
      align: "center",
      width: 90,
      render: (ativo: number) =>
        ativo === 1 ? (
          <Tag color="green">Ativo</Tag>
        ) : (
          <Tag color="red">Inativo</Tag>
        ),
    },
    {
      title: "Ações",
      key: "operation",
      align: "center",
      width: 90,
      render: (_, row) => {
        // define menu conforme estado
        const items: MenuProps["items"] = [
          { key: "edit", label: "Editar" },
          {
            key: "toggle",
            label:
              row.active === 1 ? (
                <span style={{ color: "#fa541c" }}>Desativar</span>
              ) : (
                <span style={{ color: "#389e0d" }}>Ativar</span>
              ),
          },
        ];

        return (
          <Space size="middle">
            <Dropdown
              menu={{
                items,
                onClick: ({ key }) => handleMenuClick(key, row),
              }}
              trigger={["click"]}
            >
              <BotoesAcoes>
                <Space>
                  <SettingsIcon />
                </Space>
              </BotoesAcoes>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <>
    {contextHolder}
      {isLoading || isTokenLoading ? (
        <Skeleton active />
      ) : (
        <Container>
          {contextHolder}

          {/* Cabeçalho */}
          <Row>
            <Col span={5}>
              <Flex align="center" justify="center" style={{ marginTop: 15 }}>
                <a href={`/gerenciarCategoriaMensagens`}>
                  <ArrowBackIcon color="primary" />
                </a>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" justify="center">
                <HomeIcon color="primary" fontSize="large" />
                <Title
                  style={{
                    marginLeft: 10,
                    marginTop: 10,
                    fontSize: isMobile ? "1rem" : "2rem",
                    color: "rgb(0, 54, 68)",
                  }}
                  level={3}
                >
                  Gerenciar Modelos de Mensagens
                </Title>
              </Flex>
            </Col>
          </Row>

          {/* Subtítulo com nome da categoria */}
          <Row>
            <Col span={24}>
              <Flex justify="center" style={{ marginBottom: 10 }}>
                <Typography.Text type="secondary" style={{ fontSize: "1rem" }}>
                  Categoria: <b>{categoriaNome || "Carregando..."}</b>
                </Typography.Text>
              </Flex>
            </Col>
          </Row>

          {/* Botão + Novo Tipo */}
          <Row>
            <Col span={24}>
              <Flex justify="flex-end" style={{ marginBottom: 16, marginRight: 60 }}>
                <Button type="primary" onClick={() => openModal()}>
                  + Tipo de Mensagem
                </Button>
              </Flex>

              {/* Tabela */}
              <Flex style={{ width: "100%", overflowX: "auto" }}>
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={tipos}
                  bordered
                  pagination={{ pageSize: 8 }}
                  scroll={{ x: 900 }}
                  style={{
                    width: "100%",
                    minWidth: 700,
                  }}
                />
              </Flex>
            </Col>
          </Row>

                  {/* Modal */}
        <Modal
          title={editingId ? "Editar Tipo de Mensagem" : "Novo Tipo de Mensagem"}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={handleSave}
        >
          <Form form={form} layout="vertical">
            {/* Nome */}
            <Form.Item name="nome" label="Nome" rules={[{ required: true }]}>
              <Input placeholder="Digite o nome do tipo de mensagem" />
            </Form.Item>

            {/* Descrição */}
            <Form.Item name="descricao" label="Descrição">
              <Input.TextArea rows={3} placeholder="Descreva o uso dessa mensagem" />
            </Form.Item>

            {/* Título Padrão */}
            <Form.Item name="tituloPadrao" label="Título Padrão">
              <Input placeholder="Ex.: Bem-vindo ao nosso serviço!" />
            </Form.Item>

            {/* Conteúdo Padrão */}
            <Form.Item name="conteudoPadrao" label="Conteúdo Padrão">
              <Input.TextArea
                rows={4}
                placeholder="Ex.: Olá {{nome}}, estamos felizes em ter você conosco!"
              />
            </Form.Item>

            {/* 🔹 Link Padrão */}
            <Form.Item
              name="linkPadrao"
              label="Link Padrão"
              rules={[
                {
                  type: "url",
                  message: "Informe um link válido (ex: https://meusite.com)",
                },
              ]}
            >
              <Input
                prefix={<LinkOutlined style={{ color: "rgba(0,0,0,.45)" }} />}
                placeholder="Ex.: https://meusite.com/oferta"
                allowClear
              />
            </Form.Item>

            {/* Ativo */}
            <Form.Item label="Ativo" name="active" valuePropName="checked">
              <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
            </Form.Item>
          </Form>
        </Modal>
        </Container>
      )}
    </>
  );
}
